import { produce } from 'immer';
import {
  generateId,
  getItemsAtTile,
  getItemByIdOrThrow,
  getAnchorTile,
  setWindowCursor
} from 'src/utils';
import { ModeActions, Connector as ConnectorI } from 'src/types';

export const Connector: ModeActions = {
  entry: () => {
    setWindowCursor('crosshair');
  },
  exit: () => {
    setWindowCursor('default');
  },
  mousemove: ({ uiState, scene }) => {
    if (
      uiState.mode.type !== 'CONNECTOR' ||
      !uiState.mode.id
    )
      return;

    const connector = getItemByIdOrThrow(
      scene.currentView.connectors ?? [],
      uiState.mode.id
    );

    const itemAtTile = getItemsAtTile({
      tile: uiState.mouse.position.tile,
      scene
    }).find((item) => item.type === 'ITEM');

    if (itemAtTile?.type === 'ITEM') {
      const newConnector = produce(connector.value, (draft) => {
        draft.anchors[1] = { id: generateId(), ref: { item: itemAtTile.id } };
      });

      scene.updateConnector(uiState.mode.id, newConnector);
    } else {
      const newConnector = produce(connector.value, (draft) => {
        draft.anchors[1] = {
          id: generateId(),
          ref: { tile: uiState.mouse.position.tile }
        };
      });

      scene.updateConnector(uiState.mode.id, newConnector);
    }
  },
  mousedown: ({ uiState, scene, isRendererInteraction }) => {
    if (uiState.mode.type !== 'CONNECTOR' || !isRendererInteraction) return;
    // A contextual connector handle has already created the connector.
    if (uiState.mode.id) return;

    const newConnector: ConnectorI = {
      id: generateId(),
      color: scene.colors[0].value,
      anchors: []
    };

    const itemAtTile = getItemsAtTile({
      tile: uiState.mouse.position.tile,
      scene
    }).find((item) => item.type === 'ITEM');

    if (itemAtTile && itemAtTile.type === 'ITEM') {
      newConnector.anchors = [
        { id: generateId(), ref: { item: itemAtTile.id } },
        { id: generateId(), ref: { item: itemAtTile.id } }
      ];
    } else {
      newConnector.anchors = [
        { id: generateId(), ref: { tile: uiState.mouse.position.tile } },
        { id: generateId(), ref: { tile: uiState.mouse.position.tile } }
      ];
    }

    scene.createConnector(newConnector);

    uiState.actions.setMode({
      type: 'CONNECTOR',
      showCursor: true,
      id: newConnector.id
    });
  },
  mouseup: ({ uiState, scene }) => {
    if (uiState.mode.type !== 'CONNECTOR' || !uiState.mode.id) return;

    const connector = getItemByIdOrThrow(scene.connectors, uiState.mode.id);
    const anchors = connector.value.anchors;
    const startTile = getAnchorTile(anchors[0], scene.currentView);
    const endTile = getAnchorTile(anchors[anchors.length - 1], scene.currentView);
    // Pathfinding can return duplicate path entries for a zero-length line,
    // so validate the resolved endpoints rather than the path array length.
    const isValid = startTile.x !== endTile.x || startTile.y !== endTile.y;
    if (!isValid) {
      scene.deleteConnector(uiState.mode.id);
    } else {
      uiState.actions.setItemControls({ type: 'CONNECTOR', id: uiState.mode.id });
    }

    uiState.actions.setMode({
      type: 'CURSOR',
      showCursor: true,
      mousedownItem: null
    });
  }
};
